import { CameraDistance } from '@monkvision/types';
import type { CoveredSegment } from '@monkvision/common';

/**
 * The current state of coverage tracking in the VehicleWalkaroundIndicator.
 * - `'off'`: No tracking. Covered segments are cleared.
 * - `'active'`: Tracking is ongoing. Covered segments are updated as the position changes.
 * - `'paused'`: Tracking is paused. Covered segments are retained but not updated.
 */
export enum WalkaroundTrackingState {
  OFF = 'off',
  ACTIVE = 'active',
  PAUSED = 'paused',
}

/**
 * Props accepted by the VehicleWalkaroundIndicator component.
 */
export interface VehicleWalkaroundIndicatorProps {
  /**
   * The rotation of the user around the vehicle (0-360 degrees).
   * This is calculated from the device orientation alpha and the starting position.
   * - 0: POV in the front of the car
   * - 180: POV is behind the back of the car
   */
  alpha: number;
  /**
   * The size (width and height, in pixels) of the indicator.
   *
   * @default 70
   */
  size?: number;
  /**
   * POV orientation angle:
   * - 0: POV is facing north
   * - 180: POV is facing south.
   */
  orientationAngle?: number;
  /**
   * Boolean indicating if the circle should be visible.
   *
   * @default true
   */
  showCircle?: boolean;
  /**
   * POV/Flashlight distance from the vehicle.
   *
   * @default CameraDistance.STANDARD
   */
  distance?: CameraDistance;
  /**
   * Boolean indicating if the progress bar should be visible (Circle and vehicle).
   */
  showProgressBar?: boolean;
  /**
   * Pre-computed covered segments to render as green arcs on the walkaround circle.
   *
   * @see `useVehicleWalkaroundIndicatorState` for pre-built logic.
   */
  coveredSegments?: CoveredSegment[];
  /**
   * When true, transitions the car icon to a green checkmark to signal the walkaround is complete.
   */
  showCompletionIcon?: boolean;
}
